/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { derived } from '@vscode/observables';

/**
 * Takes a value of type `T` and makes it available as `IObservable<T>`.
 * When it changes, the component will not re-render.
 */
function prop() {
    return PropertyTransformerFactory.instance;
}
/**
 * Takes a value of type `T` and makes it available as `T`.
 * When it changes, the component will re-render.
 */
function propConst() {
    return PropertyConstTransformerFactory.instance;
}
/**
 * Takes a value of type `T` or `IObservable<T>` and makes it available as `IObservable<T>`.
 * When it changes, the component will not re-render.
 */
function propObs() {
    return PropertyObsTransformerFactory.instance;
}
prop.const = propConst;
prop.obs = propObs;
class PropertyTransformerFactory {
    create(readableProp) {
        const d = derived(reader => readableProp(reader));
        return { read: (_reader) => d };
    }
}
PropertyTransformerFactory.instance = new PropertyTransformerFactory();
class PropertyConstTransformerFactory {
    create(readableProp) {
        const d = derived(reader => readableProp(reader));
        return { read: (reader) => d.read(reader) };
    }
}
PropertyConstTransformerFactory.instance = new PropertyConstTransformerFactory();
class PropertyObsTransformerFactory {
    create(readableProp) {
        const d = derived(reader => {
            const v = readableProp(reader);
            if (isObservable(v)) {
                return v.read(reader);
            }
            else {
                return v;
            }
        });
        return { read: (_reader) => d };
    }
}
PropertyObsTransformerFactory.instance = new PropertyObsTransformerFactory();
function isObservable(obs) {
    return typeof obs === "object" && obs !== null && "read" in obs;
}

export { prop };
//# sourceMappingURL=IPropertyTransformer.js.map
